<template>
  <dashboard-layout title="Astrocartography" subtitle="Five Places — queue, generation cost, the line map & report content">
    <template #actions>
      <router-link to="/cities" class="link-btn">City pool →</router-link>
      <search-box v-model="search" placeholder="Search user / report id / city…" />
      <button class="refresh-btn" @click="load" :disabled="loading">{{ loading ? 'Loading…' : 'Refresh' }}</button>
    </template>

    <div v-if="error" class="error-banner">⚠ {{ error }}</div>

    <!-- Live: runs generating right now, filling in as sections land (onSnapshot) -->
    <data-panel>
      <template #title>
        Live <span class="muted">({{ inFlight.length }})</span>
        <span class="live-dot" :class="{ on: watching }" :title="watching ? 'Watching live' : 'Connecting…'"></span>
      </template>
      <template #actions>
        <span v-if="staleCount" class="stale-flag">⚠ {{ staleCount }} stale</span>
      </template>
      <div v-if="!inFlight.length" class="live-idle">No runs generating right now — new runs appear here live as they start.</div>
      <div v-else class="live-cards">
        <div v-for="r in inFlight" :key="r.id" class="live-card" :class="{ stale: isStale(r) }">
          <div class="lc-top">
            <status-badge :variant="reportStateVariant(r)" :label="reportStateLabel(r)" />
            <span class="lc-title" :title="r.title">{{ r.user.name || r.title || 'Untitled' }}</span>
            <span class="lc-elapsed">{{ reportDuration(r) }}</span>
          </div>
          <div class="lc-meta">
            <a v-if="r.userId" :href="`/user/${r.userId}`" class="user-link" @click.prevent="openUser(r.userId, $event)">{{ truncate(r.userId, 12) }}</a>
            <span v-else class="muted">—</span>
            <span v-if="r.language"> · {{ r.language }}</span>
            <span v-if="r.places.length"> · {{ r.places.map(p => p.city).filter(Boolean).join(', ') }}</span>
          </div>
          <div class="lc-stages">
            <div v-for="sec in SLICES" :key="sec" class="stage" :class="'st-' + stageState(r, sec)">
              <span class="stage-ic">{{ stageState(r, sec) === 'ready' ? '✓' : (stageState(r, sec) === 'failed' ? '✕' : '') }}</span>
              {{ sliceLabel(sec, r) }}
            </div>
          </div>
          <div class="lc-prog"><div class="lc-prog-fill" :style="{ width: (readySections(r) / SLICES.length * 100) + '%' }"></div></div>
          <div class="lc-count">{{ readySections(r) }}/{{ SLICES.length }} sections ready</div>
        </div>
      </div>
    </data-panel>

    <loading-state v-if="loading && !generations.length && !stats && !reports.length" label="Loading Astrocartography…" />

    <template v-else>
      <!-- Overview strip: reportStats rollup + live in-flight count -->
      <stat-grid>
        <stat-card label="Reports" :value="(stats ? stats.count : 0).toLocaleString('en-US')" icon="filetext" accent>
          <template #sub>{{ stats ? stats.completed : 0 }} complete · {{ stats ? stats.partial : 0 }} partial · {{ stats ? stats.failed : 0 }} failed</template>
        </stat-card>
        <stat-card label="In progress" :value="runningCount.toLocaleString('en-US')" icon="activity">
          <template #sub v-if="staleCount">{{ staleCount }} stale (>4 min)</template>
          <template #sub v-else>generating right now</template>
        </stat-card>
        <stat-card label="Avg cost / report" :value="formatCost(avgCost)" icon="dollar">
          <template #sub>full only {{ formatCost(avgCostFull) }} · excl. repairs</template>
        </stat-card>
        <stat-card label="Total spent" :value="formatCost(stats ? stats.totalCostUSD : 0)" icon="creditcard">
          <template #sub>{{ (stats ? stats.count : 0).toLocaleString('en-US') }} generations · {{ stats ? stats.repairs : 0 }} repairs</template>
        </stat-card>
        <stat-card label="Avg duration" :value="formatMs(avgMs)" icon="sliders">
          <template #sub v-if="stats && stats.lastAtMs">last {{ formatDate(stats.lastAtMs) }}</template>
        </stat-card>
      </stat-grid>

      <p class="note">
        Headline numbers are the all-time <code>reportStats/astrocartography</code> rollup. The chart and slice
        breakdown reflect the {{ generations.length.toLocaleString('en-US') }} loaded
        generation{{ generations.length === 1 ? '' : 's' }}. Phase 0 (no model) writes the five places, the three
        directions and the full line map; the five place sections then run in parallel and the directions section
        closes after them.
      </p>

      <!-- Cost per day -->
      <data-panel v-if="generations.length" title="Cost per day (USD)">
        <div class="chart-box"><canvas ref="dayCanvas"></canvas></div>
      </data-panel>

      <!-- Cost by slice -->
      <div v-if="generations.length" class="dual-grid">
        <data-panel title="Cost by slice">
          <div class="chart-box small"><canvas ref="sliceCanvas"></canvas></div>
        </data-panel>
        <data-panel title="Avg cost & duration per slice (loaded)">
          <div class="slice-bars">
            <div v-for="s in sliceAgg" :key="s.key" class="slice-bar-row">
              <span class="sb-label">{{ sliceLabel(s.key) }}</span>
              <div class="sb-track"><div class="sb-fill" :style="{ width: s.pct + '%', background: sliceColor(s.key) }"></div></div>
              <span class="sb-val">{{ formatCost(s.avg) }}</span>
              <span class="sb-val2">{{ formatMs(s.avgMs) }}</span>
            </div>
          </div>
        </data-panel>
      </div>

      <!-- Generations (cost rows) -->
      <div class="filters">
        <filter-chips v-model="stateFilter" :options="stateOptions" />
      </div>
      <data-panel flush>
        <template #title>Generations <span class="muted">({{ filteredGenerations.length.toLocaleString('en-US') }})</span></template>
        <data-table
          :columns="genColumns"
          :rows="filteredGenerations"
          row-key="id"
          clickable
          :expanded-key="expandedGen"
          @row-click="(row) => (expandedGen = expandedGen === row.id ? null : row.id)"
        >
          <template #cell-created="{ row }">
            <span class="date-cell"><span class="caret" :class="{ open: expandedGen === row.id }">▸</span> {{ formatDate(row.createdAtMs) }}</span>
          </template>
          <template #cell-user="{ row }">
            <a v-if="row.userId" :href="`/user/${row.userId}`" class="user-link" @click.stop.prevent="openUser(row.userId, $event)">{{ truncate(row.userId, 10) }}</a>
            <span v-else class="muted">—</span>
          </template>
          <template #cell-language="{ row }">{{ row.language || '—' }}</template>
          <template #cell-state="{ row }">
            <status-badge :variant="genStateVariant(row.state)" :label="row.state || '—'" />
            <status-badge v-if="row.partialRepair" variant="info" label="repair" class="repair-tag" />
          </template>
          <template #cell-time="{ row }">
            <status-badge v-if="row.timeAccuracy" :variant="row.timeAccuracy === 'exact' ? 'success' : 'neutral'" :label="row.timeAccuracy" />
            <span v-else class="muted">—</span>
          </template>
          <template #cell-cost="{ row }"><span class="strong">{{ formatCost(row.costUsd) }}</span></template>
          <template #cell-duration="{ row }">{{ formatMs(row.totalMs) }}</template>
          <template #cell-tokens="{ row }">{{ formatTokens(row.inputTokens) }} / {{ formatTokens(row.outputTokens) }}</template>
          <template #cell-failed="{ row }">
            <span v-if="row.slicesFailed.length" class="failed">{{ row.slicesFailed.join(', ') }}</span>
            <span v-else class="muted">—</span>
          </template>
          <template #empty>No generations match your filters</template>

          <template #expanded="{ row }">
            <div class="slice-detail">
              <table class="slice-table">
                <thead>
                  <tr><th>Slice</th><th>Model</th><th>OK</th><th>Cost</th><th>Duration</th><th>In/Out</th><th>Think</th><th>Att.</th></tr>
                </thead>
                <tbody>
                  <tr v-for="key in SLICES" :key="key" :class="{ 'slice-missing': !row.slices[key] }">
                    <td class="sl-name">{{ sliceLabel(key) }}</td>
                    <td class="sl-model">{{ modelShort(sliceOf(row, key).model) }}</td>
                    <td>
                      <span v-if="row.slices[key]" :class="sliceOf(row, key).ok ? 'ok-yes' : 'ok-no'">{{ sliceOf(row, key).ok ? '✓' : '✕' }}</span>
                      <span v-else class="muted">—</span>
                    </td>
                    <td>{{ row.slices[key] ? formatCost(num(sliceOf(row, key).costUsd)) : '—' }}</td>
                    <td>{{ row.slices[key] ? formatMs(num(sliceOf(row, key).ms)) : '—' }}</td>
                    <td>{{ row.slices[key] ? (formatTokens(num(sliceOf(row, key).inputTokens)) + ' / ' + formatTokens(num(sliceOf(row, key).outputTokens))) : '—' }}</td>
                    <td>{{ row.slices[key] && sliceOf(row, key).thinkingTokens ? formatTokens(num(sliceOf(row, key).thinkingTokens)) : '—' }}</td>
                    <td>{{ row.slices[key] ? num(sliceOf(row, key).attempts) : '—' }}</td>
                  </tr>
                </tbody>
              </table>
              <div class="slice-foot">
                <span>Report: <code>{{ row.reportId || '—' }}</code></span>
                <span v-if="row.promptVersion">Prompt: <code>{{ row.promptVersion }}</code></span>
                <span v-if="row.poolSize">Pool: <code>{{ row.poolSize }}</code></span>
                <span v-if="row.places.length">Picked: <code>{{ row.places.map(p => `${p.id} ${p.score}`).join(', ') }}</code></span>
              </div>
            </div>
          </template>
        </data-table>
      </data-panel>

      <!-- Recent reports (queue + content detail) -->
      <data-panel flush>
        <template #title>Recent reports <span class="muted">({{ filteredReports.length.toLocaleString('en-US') }})</span></template>
        <template #actions>
          <span v-if="runningCount" class="running-flag">● {{ runningCount }} running</span>
          <span v-if="staleCount" class="stale-flag">⚠ {{ staleCount }} stale</span>
        </template>
        <data-table
          :columns="reportColumns"
          :rows="filteredReports"
          row-key="id"
          clickable
          :expanded-key="expandedReport"
          @row-click="(row) => (expandedReport = expandedReport === row.id ? null : row.id)"
        >
          <template #cell-created="{ row }">
            <span class="date-cell"><span class="caret" :class="{ open: expandedReport === row.id }">▸</span> {{ formatDate(row.createdAtMs) }}</span>
          </template>
          <template #cell-state="{ row }">
            <status-badge :variant="reportStateVariant(row)" :label="reportStateLabel(row)" />
            <status-badge v-if="needsRepair(row)" variant="warning" label="repair" class="repair-tag" />
          </template>
          <template #cell-user="{ row }">
            <div class="ellip" :title="row.user.name">{{ row.user.name || '—' }}</div>
            <a v-if="row.userId" :href="`/user/${row.userId}`" class="user-link small" @click.stop.prevent="openUser(row.userId, $event)">{{ truncate(row.userId, 10) }}</a>
          </template>
          <template #cell-places="{ row }">
            <div v-if="row.places.length" class="place-mini">
              <span v-for="p in row.places" :key="p.rank" class="pm-row">
                <span class="pm-rank">{{ p.rank }}</span>
                <span class="pm-name" :title="`${p.city}, ${p.country}`">{{ p.flag }} {{ truncate(p.city, 16) }}</span>
                <span class="pm-score">{{ p.score }}</span>
                <status-badge :variant="bucketVariant(p.bucket)" :label="p.bucket || '—'" />
              </span>
            </div>
            <span v-else class="muted">—</span>
          </template>
          <template #cell-time="{ row }">
            <status-badge v-if="row.timeAccuracy" :variant="row.timeAccuracy === 'exact' ? 'success' : 'neutral'" :label="row.timeAccuracy" />
            <span v-else class="muted">—</span>
          </template>
          <template #cell-sections="{ row }">
            <span class="dots">
              <span v-for="sec in SLICES" :key="sec" class="dot" :class="'d-' + sectionState(row, sec)" :title="sliceLabel(sec, row) + ': ' + (row.sections[sec] || 'pending')"></span>
            </span>
          </template>
          <template #cell-duration="{ row }">{{ reportDuration(row) }}</template>
          <template #cell-cost="{ row }">
            <span v-if="genForReport(row.reportId)" class="strong">{{ formatCost(genForReport(row.reportId).costUsd) }}</span>
            <span v-else class="muted">—</span>
          </template>
          <template #empty>No astrocartography reports yet.</template>

          <template #expanded="{ row }">
            <div class="rep-detail">
              <!-- reader -->
              <div class="rd-section">
                <div class="rd-title">{{ row.title || row.user.name || 'Reader' }} <span class="muted">· {{ row.language || '—' }} · {{ row.timeAccuracy || '—' }} time</span></div>
                <div class="reader-three">
                  <span class="three-pill"><span>Sun</span><b>{{ row.user.sun || '—' }}</b></span>
                  <span class="three-pill"><span>Moon</span><b>{{ row.user.moon || '—' }}</b></span>
                  <span class="three-pill"><span>Rising</span><b>{{ row.user.rising || '—' }}</b></span>
                  <span v-if="row.user.dominant" class="three-pill"><span>Dominant</span><b>{{ row.user.dominant }}</b></span>
                  <span v-if="row.user.birthDate" class="three-pill"><span>Born</span><b>{{ row.user.birthDate }}</b></span>
                  <span v-if="row.user.birthPlace" class="three-pill"><span>in</span><b>{{ row.user.birthPlace }}</b></span>
                  <span v-if="row.birth.latitude != null" class="three-pill"><span>at</span><b>{{ fmtCoord(row.birth.latitude, row.birth.longitude) }}</b></span>
                </div>
              </div>

              <!-- the map -->
              <div class="rd-section">
                <div class="rd-title">
                  Line map
                  <span class="muted">· {{ row.lines.curves.length }} lines · orb {{ row.lines.orbKm || '—' }} km · birthplace exclusion {{ row.lines.birthplaceExclusionKm || '—' }} km<template v-if="row.lines.gst"> · GST {{ row.lines.gst }}°</template></span>
                </div>
                <world-map
                  v-if="row.lines.curves.length || row.places.length"
                  :curves="row.lines.curves"
                  :places="mapPlaces(row)"
                  :birth="row.birth.latitude != null ? { lat: row.birth.latitude, lon: row.birth.longitude, title: 'Birthplace · ' + (row.user.birthPlace || '') } : null"
                  :dots="poolDots"
                  :active-key="activePlaceKey(row)"
                  @place-click="(p) => focusPlace(row, p.rank)"
                />
                <div v-else class="muted small">No line map on this report.</div>
              </div>

              <!-- five places: computed in phase 0 -->
              <div class="rd-section">
                <div class="rd-title">Five places <span class="muted">· pool {{ row.poolSize || '—' }}</span></div>
                <div class="table-scroll">
                  <table class="places-table">
                    <thead>
                      <tr><th>#</th><th></th><th>City</th><th class="num">Score</th><th>Bucket</th><th class="num">From birth</th><th>Lines</th><th>Cautions</th></tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="p in row.places"
                        :key="p.rank"
                        :class="{ active: openPlace[row.id] === p.rank }"
                        @click="focusPlace(row, p.rank)"
                      >
                        <td class="pt-rank">{{ p.rank }}</td>
                        <td>
                          <div class="city-photo">
                            <img v-if="p.photo && p.photo.url" :src="p.photo.url" :alt="p.city" loading="lazy" referrerpolicy="no-referrer" :title="photoCredit(p.photo)" @error="onImgError">
                            <div v-else class="city-nophoto">{{ p.flag || (p.city || '?').slice(0, 1) }}</div>
                          </div>
                        </td>
                        <td>
                          <div class="pt-city">{{ p.flag }} {{ p.city || '—' }}</div>
                          <div class="pt-country">{{ p.country }} · {{ fmtCoord(p.latitude, p.longitude) }}</div>
                        </td>
                        <td class="num strong">{{ p.score }}</td>
                        <td><status-badge :variant="bucketVariant(p.bucket)" :label="p.bucket || '—'" /></td>
                        <td class="num">{{ formatKm(p.distanceFromBirthKm) }}</td>
                        <td>
                          <div class="line-chips">
                            <span v-for="(l, li) in p.lines" :key="li" class="line-chip" :class="'tone-' + (l.tone || 'none')" :title="`${l.tone || ''}${l.bucket ? ' · ' + l.bucket : ''}`">{{ l.label }}</span>
                            <span v-if="!p.lines.length" class="muted">—</span>
                          </div>
                        </td>
                        <td>
                          <div class="line-chips">
                            <span v-for="(l, li) in p.cautions" :key="li" class="line-chip tone-challenging">{{ l.label }}</span>
                            <span v-if="!p.cautions.length" class="muted">—</span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- three directions (career / relationship / home) -->
              <div class="rd-section">
                <div class="rd-title">Directions <span class="muted">· {{ sectionState(row, 'directions') }}</span></div>
                <div class="dir-cards">
                  <div v-for="d in directionCards(row)" :key="d.kind" class="dir-card">
                    <div class="dir-head">
                      <span class="dir-kind">{{ d.kind }}</span>
                      <span class="dir-city">{{ d.flag }} {{ d.city || d.cityId || '—' }}</span>
                    </div>
                    <template v-if="d.prose">
                      <div class="dir-title">{{ d.prose.title }}</div>
                      <p class="mc-note dir-lead">{{ d.prose.lead }}</p>
                      <p v-for="(para, pi) in (d.prose.paragraphs || [])" :key="pi" class="mc-body">{{ para }}</p>
                      <div v-if="d.prose.chips && d.prose.chips.length" class="trait-row">
                        <span v-for="(c, ci) in d.prose.chips" :key="ci" class="trait-chip">{{ c }}</span>
                      </div>
                    </template>
                    <div v-else class="mc-pending">Prose not written yet.</div>
                  </div>
                </div>
              </div>

              <!-- per-place prose -->
              <div class="rd-section">
                <div class="rd-title">Place sections</div>
                <div class="place-cards">
                  <div
                    v-for="p in row.places"
                    :key="p.rank"
                    :ref="(el) => setPlaceRef(row.id, p.rank, el)"
                    class="place-card"
                    :class="{ open: openPlace[row.id] === p.rank }"
                  >
                    <div class="pc-head" @click="togglePlace(row.id, p.rank)">
                      <span class="caret" :class="{ open: openPlace[row.id] === p.rank }">▸</span>
                      <span class="pc-rank">{{ p.rank }}</span>
                      <span class="pc-city">{{ p.flag }} {{ p.city }}</span>
                      <span class="pc-theme">{{ placeProse(row, p.rank) ? placeProse(row, p.rank).theme : '' }}</span>
                      <span class="pc-state" :class="'ps-' + sectionState(row, 'place' + p.rank)">{{ sectionState(row, 'place' + p.rank) }}</span>
                    </div>
                    <div v-if="openPlace[row.id] === p.rank" class="pc-body">
                      <template v-if="placeProse(row, p.rank)">
                        <div v-if="(placeProse(row, p.rank).themes || []).length" class="trait-row">
                          <span v-for="(t, ti) in placeProse(row, p.rank).themes" :key="ti" class="trait-chip">{{ t }}</span>
                        </div>

                        <div class="mc-mini-title">{{ placeProse(row, p.rank).lifeHeadline }}</div>
                        <p class="mc-body">{{ placeProse(row, p.rank).lifeBody }}</p>

                        <div class="mc-mini-title">{{ placeProse(row, p.rank).whyLead }}</div>
                        <p class="mc-body">{{ placeProse(row, p.rank).whyBody }}</p>

                        <div v-if="(placeProse(row, p.rank).chartLines || []).length" class="chart-lines">
                          <div v-for="(cl, ci) in placeProse(row, p.rank).chartLines" :key="ci" class="cl-row">
                            <span class="cl-name">{{ cl.name }}</span>
                            <span class="cl-km muted">{{ cl.planet }} {{ cl.angle }} · {{ formatKm(cl.km) }}</span>
                            <span class="cl-detail">{{ cl.detail }}</span>
                          </div>
                        </div>

                        <div class="mc-mini-title">{{ placeProse(row, p.rank).developHeadline }}</div>
                        <div v-for="(dv, di) in (placeProse(row, p.rank).developments || [])" :key="di" class="dev-row">
                          <b>{{ dv.title }}</b> <span class="mc-note">{{ dv.detail }}</span>
                        </div>

                        <div class="mc-mini-title">{{ placeProse(row, p.rank).readyHeadline }}</div>
                        <p class="mc-body">{{ placeProse(row, p.rank).readyBody }}</p>

                        <div class="mc-mini-title">{{ placeProse(row, p.rank).makeHeadline }}</div>
                        <ul v-if="(placeProse(row, p.rank).steps || []).length" class="try-list">
                          <li v-for="(s, si) in placeProse(row, p.rank).steps" :key="si">{{ s }}</li>
                        </ul>
                      </template>
                      <div v-else class="mc-pending">Section <b>{{ sectionState(row, 'place' + p.rank) }}</b> — prose not written yet.</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- per-slice timing/cost from the generation row -->
              <div class="rd-section" v-if="genForReport(row.reportId)">
                <div class="rd-title">Generation <span class="muted">· {{ formatCost(genForReport(row.reportId).costUsd) }} · {{ formatMs(genForReport(row.reportId).totalMs) }}<template v-if="genForReport(row.reportId).partialRepair"> · repair run</template></span></div>
                <div class="parts-grid">
                  <span v-for="sec in SLICES" :key="sec" class="part-chip" :class="'ps-' + sectionState(row, sec)">
                    {{ sliceLabel(sec, row) }}
                    <template v-if="sliceOf(genForReport(row.reportId), sec).costUsd"> · {{ formatCost(num(sliceOf(genForReport(row.reportId), sec).costUsd)) }}</template>
                    <template v-if="sliceOf(genForReport(row.reportId), sec).ms"> · {{ formatMs(num(sliceOf(genForReport(row.reportId), sec).ms)) }}</template>
                    <span v-if="row.sectionsReadyAtMs[sec]" class="part-at">{{ formatClock(row.sectionsReadyAtMs[sec]) }}</span>
                  </span>
                </div>
                <div class="rd-run-meta">
                  <span>Report <code>{{ row.reportId }}</code></span>
                  <span v-if="row.promptVersion">Prompt <code>{{ row.promptVersion }}</code></span>
                </div>
              </div>
              <div class="rd-section" v-else>
                <div class="rd-title">Run</div>
                <div class="parts-grid">
                  <span v-for="sec in SLICES" :key="sec" class="part-chip" :class="'ps-' + sectionState(row, sec)">
                    {{ sliceLabel(sec, row) }}<span v-if="row.sectionsReadyAtMs[sec]" class="part-at">{{ formatClock(row.sectionsReadyAtMs[sec]) }}</span>
                  </span>
                </div>
                <div class="rd-run-meta">
                  <span>Report <code>{{ row.reportId }}</code></span>
                  <span v-if="row.promptVersion">Prompt <code>{{ row.promptVersion }}</code></span>
                </div>
              </div>
            </div>
          </template>
        </data-table>
      </data-panel>

      <!-- Errors -->
      <data-panel flush>
        <template #title>Errors <span class="muted">({{ errorLogs.length }})</span> <span class="muted small">· last 7 days</span></template>
        <data-table :columns="errorColumns" :rows="errorLogs" row-key="id">
          <template #cell-time="{ row }"><span class="nowrap">{{ formatDate(row.createdAtMs) }}</span></template>
          <template #cell-level="{ row }"><status-badge :variant="row.level === 'error' ? 'danger' : 'warning'" :label="row.level" /></template>
          <template #cell-message="{ row }"><span class="ellip wide" :title="row.message">{{ row.message || '—' }}</span></template>
          <template #cell-error="{ row }"><span class="ellip" :title="row.error">{{ row.error || '—' }}</span></template>
          <template #cell-failed="{ row }">
            <span v-if="row.failedSlices" class="failed">{{ Array.isArray(row.failedSlices) ? row.failedSlices.join(', ') : row.failedSlices }}</span>
            <span v-else class="muted">—</span>
          </template>
          <template #cell-reportId="{ row }"><span class="mono">{{ truncate(row.reportId, 14) || '—' }}</span></template>
          <template #empty>No errors in the last 7 days for generateAstrocartographyReport. 🎉</template>
        </data-table>
      </data-panel>
    </template>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { Chart } from 'chart.js/auto'
import { FirebaseAstrocartographyReportService, MAP_SLICES, PLACE_SLICES } from '../services/firebaseAstrocartographyReportService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import DataTable from './ui/DataTable.vue'
import SearchBox from './ui/SearchBox.vue'
import FilterChips from './ui/FilterChips.vue'
import LoadingState from './ui/LoadingState.vue'
import StatusBadge from './ui/StatusBadge.vue'
import WorldMap from './ui/WorldMap.vue'

const SLICE_COLORS = { place1: '#6c63ff', place2: '#22d3ee', place3: '#f8bf71', place4: '#f472b6', place5: '#a78bfa', directions: '#4ade80' }
const SLICE_LABELS = { place1: 'Place 1', place2: 'Place 2', place3: 'Place 3', place4: 'Place 4', place5: 'Place 5', directions: 'Directions' }
const DIRECTION_KINDS = ['career', 'relationship', 'home']
const STALE_MS = 4 * 60 * 1000 // backend treats a run older than 4 min as dead

const genColumns = [
  { key: 'created', label: 'Created', width: '160px' },
  { key: 'user', label: 'User', width: '100px' },
  { key: 'language', label: 'Lang', width: '60px' },
  { key: 'state', label: 'State', width: '130px' },
  { key: 'time', label: 'Birth time', width: '110px' },
  { key: 'cost', label: 'Cost', width: '90px' },
  { key: 'duration', label: 'Duration', width: '90px' },
  { key: 'tokens', label: 'Tok in/out', width: '120px' },
  { key: 'failed', label: 'Failed slices' }
]
const reportColumns = [
  { key: 'created', label: 'Created', width: '160px' },
  { key: 'state', label: 'State', width: '120px' },
  { key: 'user', label: 'User', width: '130px' },
  { key: 'places', label: 'Places' },
  { key: 'time', label: 'Birth time', width: '110px' },
  { key: 'sections', label: 'Sections', width: '90px' },
  { key: 'duration', label: 'Duration', width: '90px' },
  { key: 'cost', label: 'Cost', width: '90px' }
]
const errorColumns = [
  { key: 'time', label: 'Time', width: '150px' },
  { key: 'level', label: 'Level', width: '80px' },
  { key: 'message', label: 'Message' },
  { key: 'error', label: 'Error', width: '220px' },
  { key: 'failed', label: 'Failed', width: '120px' },
  { key: 'reportId', label: 'Report', width: '130px' }
]

function num(v) {
  if (typeof v === 'number') return v
  if (v == null) return 0
  const n = Number(v)
  return isNaN(n) ? 0 : n
}

export default {
  name: 'AstrocartographyReportsDashboard',
  components: { DashboardLayout, StatGrid, StatCard, DataPanel, DataTable, SearchBox, FilterChips, LoadingState, StatusBadge, WorldMap },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const service = new FirebaseAstrocartographyReportService()

    const stats = ref(null)
    const generations = ref([])
    const reports = ref([])
    const cities = ref([])
    const errorLogs = ref([])
    const loading = ref(true)
    const error = ref('')
    const search = ref('')
    const stateFilter = ref('all')
    const expandedGen = ref(null)
    const expandedReport = ref(null)
    const openPlace = ref({}) // reportId → open place rank
    const now = ref(Date.now())
    const watching = ref(false)

    const dayCanvas = ref(null)
    const sliceCanvas = ref(null)
    let dayChart = null
    let sliceChart = null
    let ticker = null
    let unsub = null
    const placeEls = {}

    // Stats, generations, cities and errors are one-shot (Refresh re-pulls them).
    // Reports are watched live so in-flight runs fill in while watched.
    const load = async () => {
      loading.value = true
      error.value = ''
      try {
        const [s, g, e, c] = await Promise.all([service.getStats(), service.getGenerations(), service.getErrorLogs(), service.getCities()])
        stats.value = s
        generations.value = g
        errorLogs.value = e
        cities.value = c
      } catch (e) {
        console.error(e)
        const code = e && e.code ? e.code : ''
        if (code === 'permission-denied') error.value = 'Firestore denied the read (not signed in). Reload and try again.'
        else if (code === 'failed-precondition') error.value = (e && e.message) || 'A Firestore index is required for this query.'
        else error.value = (e && e.message) || 'Failed to load Astrocartography.'
      } finally {
        loading.value = false
      }
    }

    const startWatch = () => {
      unsub = service.watchReports(
        rows => {
          reports.value = rows
          watching.value = true
          // ?report=<id> opens that report once it arrives.
          const want = route.query.report
          if (want && !expandedReport.value && rows.some(r => r.id === want)) expandedReport.value = want
        },
        err => { watching.value = false; error.value = (err && err.message) || 'Live report watch failed.' }
      )
    }

    const avgCost = computed(() => (stats.value && stats.value.count) ? stats.value.totalCostUSD / stats.value.count : 0)
    const avgCostFull = computed(() => {
      if (!stats.value) return 0
      const full = stats.value.count - stats.value.repairs
      return full > 0 ? stats.value.totalCostUSD / full : 0
    })
    const avgMs = computed(() => (stats.value && stats.value.count) ? stats.value.totalMs / stats.value.count : 0)

    const stateOptions = computed(() => {
      const c = (s) => generations.value.filter(g => g.state === s).length
      return [
        { value: 'all', label: 'All', count: generations.value.length },
        { value: 'complete', label: 'Complete', count: c('complete') },
        { value: 'partial', label: 'Partial', count: c('partial') },
        { value: 'failed', label: 'Failed', count: c('failed') }
      ]
    })

    const filteredGenerations = computed(() => {
      const q = search.value.toLowerCase().trim()
      return generations.value.filter(g => {
        if (stateFilter.value !== 'all' && g.state !== stateFilter.value) return false
        if (q) {
          const hay = `${g.userId} ${g.reportId} ${g.language} ${g.places.map(p => p.id).join(' ')}`.toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      })
    })

    const filteredReports = computed(() => {
      const q = search.value.toLowerCase().trim()
      if (!q) return reports.value
      return reports.value.filter(r =>
        `${r.userId} ${r.reportId} ${r.title} ${r.user.name || ''} ${r.places.map(p => `${p.city} ${p.country} ${p.cityId}`).join(' ')}`.toLowerCase().includes(q)
      )
    })

    // Pool as faint background dots on every report map (geographic context).
    const poolDots = computed(() =>
      cities.value
        .filter(c => c.latitude != null && c.longitude != null)
        .map(c => ({ lat: c.latitude, lon: c.longitude, title: `${c.flag} ${c.name}`, variant: 'muted', r: 0.8 }))
    )

    const genForReport = (reportId) => reportId ? generations.value.find(g => g.reportId === reportId) || null : null

    const inFlightState = (r) => r.state === 'creating' || r.state === 'generating'
    const isRunning = (r) => inFlightState(r) && r.startedAtMs > 0 && (now.value - r.startedAtMs) <= STALE_MS
    const isStale = (r) => inFlightState(r) && (!r.startedAtMs || (now.value - r.startedAtMs) > STALE_MS)
    const runningCount = computed(() => reports.value.filter(isRunning).length)
    const staleCount = computed(() => reports.value.filter(isStale).length)
    const needsRepair = (r) => r.state === 'partial' || r.state === 'failed'

    const inFlight = computed(() =>
      reports.value.filter(inFlightState).slice().sort((a, b) => (a.startedAtMs || a.createdAtMs) - (b.startedAtMs || b.createdAtMs))
    )
    const readySections = (r) => MAP_SLICES.filter(k => r.sections[k] === 'ready').length
    // The five places run in parallel, so every not-yet-ready place counts as
    // active; directions only after the places land.
    const stageState = (r, sec) => {
      const v = r.sections[sec]
      if (v === 'ready') return 'ready'
      if (v === 'failed') return 'failed'
      if (!isRunning(r)) return 'pending'
      if (sec === 'directions') {
        const placesDone = PLACE_SLICES.every(k => r.sections[k] === 'ready' || r.sections[k] === 'failed')
        return placesDone ? 'active' : 'pending'
      }
      return 'active'
    }

    const mapPlaces = (r) => r.places.map(p => ({
      key: String(p.rank),
      rank: p.rank,
      lat: p.latitude,
      lon: p.longitude,
      label: String(p.rank),
      title: `${p.rank}. ${p.city}, ${p.country} · ${p.score} · ${p.bucket}`
    }))
    const activePlaceKey = (r) => openPlace.value[r.id] ? String(openPlace.value[r.id]) : ''

    const placeProse = (r, rank) => {
      const s = r.content['place' + rank]
      return (s && typeof s === 'object') ? s : null
    }

    // Join directions {career|relationship|home → cityId} with the places and
    // the directions section's prose cards.
    const directionCards = (r) => {
      const prose = Array.isArray(r.content.directions) ? r.content.directions : []
      return DIRECTION_KINDS.map(kind => {
        const cityId = r.directions[kind] || ''
        const place = r.places.find(p => p.cityId === cityId) || null
        const card = prose.find(d => d && (d.kind === kind || d.id === kind + '_direction')) || null
        return {
          kind,
          cityId,
          city: (card && card.city) || (place && place.city) || '',
          flag: (card && card.flag) || (place && place.flag) || '',
          prose: card
        }
      })
    }

    const setPlaceRef = (rowId, rank, el) => { if (el) placeEls[`${rowId}:${rank}`] = el }
    const togglePlace = (rowId, rank) => {
      openPlace.value = { ...openPlace.value, [rowId]: openPlace.value[rowId] === rank ? null : rank }
    }
    // From the map pin or the places table: open that place's prose and scroll to it.
    const focusPlace = (row, rank) => {
      openPlace.value = { ...openPlace.value, [row.id]: rank }
      nextTick(() => {
        const el = placeEls[`${row.id}:${rank}`]
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      })
    }

    const sliceAgg = computed(() => {
      const totals = {}
      const ms = {}
      const counts = {}
      MAP_SLICES.forEach(k => { totals[k] = 0; ms[k] = 0; counts[k] = 0 })
      generations.value.forEach(g => {
        MAP_SLICES.forEach(k => {
          const sl = g.slices && g.slices[k]
          if (sl) { totals[k] += num(sl.costUsd); ms[k] += num(sl.ms); counts[k] += 1 }
        })
      })
      const rows = MAP_SLICES.map(k => ({ key: k, total: totals[k], avg: counts[k] ? totals[k] / counts[k] : 0, avgMs: counts[k] ? ms[k] / counts[k] : 0 }))
      const maxAvg = Math.max(...rows.map(r => r.avg), 0.0001)
      return rows.map(r => ({ ...r, pct: Math.round((r.avg / maxAvg) * 100) }))
    })

    const daySeries = computed(() => {
      const map = {}
      generations.value.forEach(g => {
        if (!g.createdAtMs) return
        const key = new Date(g.createdAtMs).toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' })
        map[key] = (map[key] || 0) + g.costUsd
      })
      return Object.keys(map).sort().map(date => ({ date, cost: map[date] }))
    })

    const destroyCharts = () => { [dayChart, sliceChart].forEach(c => c && c.destroy()); dayChart = sliceChart = null }

    const legendDark = { labels: { color: '#cfcfe1', padding: 12, font: { size: 12 }, usePointStyle: true } }
    const tooltipDark = { backgroundColor: '#1a1b2e', titleColor: '#fff', bodyColor: '#a9a9b3', borderColor: '#2c2d42', borderWidth: 1, padding: 12 }
    const baseScales = {
      x: { grid: { display: false }, ticks: { color: '#8b8b8b', font: { size: 11 }, maxRotation: 0, autoSkip: true } },
      y: { beginAtZero: true, grid: { color: '#2a2a2a' }, ticks: { color: '#8b8b8b', font: { size: 11 } } }
    }

    const renderCharts = () => {
      destroyCharts()
      if (!generations.value.length) return
      if (dayCanvas.value) {
        const series = daySeries.value
        dayChart = new Chart(dayCanvas.value.getContext('2d'), {
          type: 'bar',
          data: { labels: series.map(d => d.date), datasets: [{ label: 'Cost (USD)', data: series.map(d => +d.cost.toFixed(6)), backgroundColor: 'rgba(108,99,255,0.65)', borderColor: '#6c63ff', borderWidth: 1, borderRadius: 4 }] },
          options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { ...tooltipDark, callbacks: { label: c => ' $' + (c.parsed.y || 0).toFixed(4) } } }, scales: { ...baseScales, y: { ...baseScales.y, ticks: { ...baseScales.y.ticks, callback: v => '$' + v } } } }
        })
      }
      if (sliceCanvas.value) {
        const agg = sliceAgg.value.filter(s => s.total > 0)
        sliceChart = new Chart(sliceCanvas.value.getContext('2d'), {
          type: 'doughnut',
          data: { labels: agg.map(s => sliceLabel(s.key)), datasets: [{ data: agg.map(s => +s.total.toFixed(6)), backgroundColor: agg.map(s => sliceColor(s.key)), borderColor: '#1a1b2e', borderWidth: 2 }] },
          options: { responsive: true, maintainAspectRatio: false, cutout: '60%', plugins: { legend: { position: 'bottom', ...legendDark }, tooltip: { callbacks: { label: c => ` ${c.label}: $${(c.parsed || 0).toFixed(4)}` } } } }
        })
      }
    }

    const sliceOf = (row, key) => (row && row.slices && row.slices[key]) || {}
    const sectionState = (row, sec) => {
      const v = row.sections[sec]
      if (v === 'ready') return 'ready'
      if (v === 'failed') return 'failed'
      return 'pending'
    }
    const genStateVariant = (s) => ({ complete: 'success', partial: 'warning', failed: 'danger', generating: 'info', creating: 'info' }[s] || 'neutral')
    const reportStateVariant = (r) => {
      if (isStale(r)) return 'danger'
      if (isRunning(r)) return 'info'
      return { complete: 'success', partial: 'warning', failed: 'danger' }[r.state] || 'neutral'
    }
    const reportStateLabel = (r) => {
      if (isStale(r)) return 'stale'
      if (isRunning(r)) return 'running'
      return r.state || '—'
    }
    const bucketVariant = (b) => ({ career: 'primary', visibility: 'warning', relationships: 'info', home: 'success', growth: 'neutral' }[b] || 'neutral')
    const reportDuration = (r) => {
      if (r.startedAtMs && r.finishedAtMs && r.finishedAtMs >= r.startedAtMs) return formatMs(r.finishedAtMs - r.startedAtMs)
      if (r.startedAtMs && (isRunning(r) || isStale(r))) return formatMs(now.value - r.startedAtMs) + ' …'
      return '—'
    }

    // "Place 3" becomes "Place 3 · Lisbon" when the report row is known.
    const sliceLabel = (k, r) => {
      const base = SLICE_LABELS[k] || (k ? k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—')
      if (r && k && k.startsWith('place')) {
        const p = r.places.find(x => 'place' + x.rank === k)
        if (p && p.city) return `${base} · ${p.city}`
      }
      return base
    }
    const sliceColor = (k) => SLICE_COLORS[k] || '#8b8b9e'
    const modelShort = (m) => !m ? '—' : m.replace('claude-', '').replace(/-\d{8}$/, '')
    const truncate = (t, max) => !t ? '' : (t.length > max ? t.slice(0, max) + '…' : t)
    const formatCost = n => '$' + (n || 0).toFixed(n && n < 0.01 ? 6 : 4)
    const formatTokens = n => { const v = n || 0; if (v < 1000) return String(v); if (v < 1e6) return (v / 1e3).toFixed(1) + 'K'; return (v / 1e6).toFixed(2) + 'M' }
    const formatMs = ms => { const v = Math.round(ms || 0); if (v < 1000) return v + 'ms'; if (v < 60000) return (v / 1000).toFixed(1) + 's'; const m = Math.floor(v / 60000); const s = Math.round((v % 60000) / 1000); return s ? `${m}m ${s}s` : `${m}m` }
    const formatKm = km => (km || km === 0) ? Math.round(num(km)).toLocaleString('en-US') + ' km' : '—'
    const fmtCoord = (lat, lon) => (lat == null || lon == null) ? '—' : `${Math.abs(lat).toFixed(1)}°${lat >= 0 ? 'N' : 'S'} ${Math.abs(lon).toFixed(1)}°${lon >= 0 ? 'E' : 'W'}`
    const formatDate = ms => { if (!ms) return '—'; return new Date(ms).toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) }
    const formatClock = ms => { if (!ms) return ''; return new Date(ms).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) }
    const photoCredit = (ph) => ph ? [ph.artist, ph.license].filter(Boolean).join(' · ') : ''
    const openUser = (uid, event) => { if (event.metaKey || event.ctrlKey) window.open(`/user/${uid}`, '_blank'); else router.push(`/user/${uid}`) }
    const onImgError = (e) => { const el = e.target; el.style.display = 'none'; if (el.parentElement) el.parentElement.classList.add('img-failed') }

    watch([generations], () => renderCharts(), { flush: 'post' })

    onMounted(() => {
      load()
      startWatch()
      ticker = setInterval(() => { now.value = Date.now() }, 1000)
    })
    onBeforeUnmount(() => { if (ticker) clearInterval(ticker); if (unsub) unsub(); destroyCharts() })

    return {
      stats, generations, reports, errorLogs, loading, error, search, stateFilter, expandedGen, expandedReport, watching, openPlace,
      genColumns, reportColumns, errorColumns, SLICES: MAP_SLICES,
      avgCost, avgCostFull, avgMs, stateOptions, filteredGenerations, filteredReports, sliceAgg, poolDots,
      runningCount, staleCount, needsRepair, inFlight, readySections, stageState, isRunning, isStale, dayCanvas, sliceCanvas,
      genForReport, mapPlaces, activePlaceKey, placeProse, directionCards, setPlaceRef, togglePlace, focusPlace,
      load, sliceOf, num, sectionState, genStateVariant, reportStateVariant, reportStateLabel, bucketVariant, reportDuration,
      sliceLabel, sliceColor, modelShort, truncate, formatCost, formatTokens, formatMs, formatKm, fmtCoord, formatDate, formatClock,
      photoCredit, openUser, onImgError
    }
  }
}
</script>

<style scoped>
.error-banner { background: rgba(248, 191, 113, 0.12); border: 1px solid rgba(248, 191, 113, 0.35); color: var(--warning); border-radius: 8px; padding: 10px 14px; font-size: 13px; }
.refresh-btn { padding: 9px 14px; background: var(--field-bg); border: 1px solid var(--panel-border); border-radius: var(--radius); color: var(--text-primary); font-size: 13px; cursor: pointer; }
.refresh-btn:hover { background: var(--hover-bg); }
.refresh-btn:disabled { opacity: 0.5; cursor: default; }
.link-btn { padding: 9px 14px; background: var(--field-bg); border: 1px solid var(--panel-border); border-radius: var(--radius); color: var(--primary-color); font-size: 13px; text-decoration: none; white-space: nowrap; }
.link-btn:hover { background: var(--hover-bg); }

.note { margin: 0; font-size: 12px; color: var(--text-secondary); }
.note code { font-size: 11px; background: var(--field-bg); padding: 1px 5px; border-radius: 4px; }

.chart-box { height: 300px; position: relative; }
.chart-box.small { height: 280px; }
.dual-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 16px; }

.slice-bars { display: flex; flex-direction: column; gap: 10px; }
.slice-bar-row { display: flex; align-items: center; gap: 10px; }
.sb-label { width: 84px; flex-shrink: 0; font-size: 12.5px; color: var(--text-primary); }
.sb-track { flex: 1; height: 8px; background: rgba(255, 255, 255, 0.06); border-radius: 4px; overflow: hidden; }
.sb-fill { height: 100%; border-radius: 4px; }
.sb-val { width: 74px; text-align: right; font-size: 12px; color: var(--text-secondary); font-variant-numeric: tabular-nums; }
.sb-val2 { width: 58px; text-align: right; font-size: 11.5px; color: var(--text-secondary); font-variant-numeric: tabular-nums; }

.filters { display: flex; gap: 12px; flex-wrap: wrap; }

.muted { color: var(--text-secondary); font-weight: 400; }
.small { font-size: 11px; }
.strong { font-weight: 600; }
.mono { font-family: monospace; font-size: 12px; }
.nowrap { white-space: nowrap; }
.date-cell { white-space: nowrap; }
.ellip { display: inline-block; max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: middle; }
.ellip.wide { max-width: 360px; }
.caret { display: inline-block; width: 11px; color: var(--text-secondary); transition: transform 0.18s ease; }
.caret.open { transform: rotate(90deg); color: var(--primary-color); }
.user-link { color: var(--primary-color); text-decoration: none; }
.user-link.small { font-size: 11px; }
.user-link:hover { text-decoration: underline; }
.repair-tag { margin-left: 6px; }
.failed { color: var(--danger); font-size: 12px; }
.running-flag { font-size: 12px; color: var(--info); font-weight: 600; }
.stale-flag { font-size: 12px; color: var(--danger); font-weight: 600; margin-left: 10px; }

/* places mini-list (queue row) */
.place-mini { display: flex; flex-direction: column; gap: 3px; }
.pm-row { display: flex; align-items: center; gap: 6px; font-size: 12px; }
.pm-rank { width: 12px; color: var(--text-secondary); font-variant-numeric: tabular-nums; }
.pm-name { color: var(--text-primary); min-width: 120px; }
.pm-score { font-weight: 600; font-variant-numeric: tabular-nums; width: 24px; text-align: right; }

/* Live section */
.live-dot { display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: var(--text-secondary); opacity: 0.5; margin-left: 8px; vertical-align: middle; }
.live-dot.on { background: var(--success); opacity: 1; box-shadow: 0 0 0 3px rgba(74, 222, 128, 0.2); animation: pulse 2s infinite; }
@keyframes pulse { 50% { opacity: 0.5; } }
.live-idle { padding: 16px 4px; color: var(--text-secondary); font-size: 13px; }
.live-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 14px; }
.live-card { border: 1px solid var(--panel-border); border-radius: var(--radius); background: var(--field-bg); padding: 14px 16px; }
.live-card.stale { border-color: rgba(255, 107, 107, 0.4); background: rgba(255, 107, 107, 0.06); }
.lc-top { display: flex; align-items: center; gap: 8px; }
.lc-title { font-weight: 600; font-size: 14px; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lc-elapsed { margin-left: auto; font-size: 12px; font-variant-numeric: tabular-nums; color: var(--text-secondary); white-space: nowrap; }
.lc-meta { margin-top: 6px; font-size: 12px; color: var(--text-secondary); }
.lc-stages { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; }
.stage { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; padding: 3px 9px; border-radius: 12px; border: 1px solid var(--panel-border); background: var(--panel-bg); color: var(--text-secondary); transition: all 0.25s ease; }
.stage .stage-ic { font-size: 10px; font-weight: 700; }
.stage.st-ready { background: rgba(74, 222, 128, 0.14); color: var(--success); border-color: rgba(74, 222, 128, 0.32); }
.stage.st-failed { background: rgba(255, 107, 107, 0.16); color: var(--danger); border-color: rgba(255, 107, 107, 0.38); }
.stage.st-active { background: rgba(108, 99, 255, 0.18); color: var(--primary-color); border-color: rgba(108, 99, 255, 0.45); animation: pulse 1.3s infinite; }
.lc-prog { height: 6px; background: rgba(255, 255, 255, 0.08); border-radius: 4px; overflow: hidden; margin-top: 12px; }
.lc-prog-fill { height: 100%; background: linear-gradient(90deg, var(--primary-color), var(--gradient-2)); border-radius: 4px; transition: width 0.4s ease; }
.lc-count { margin-top: 6px; font-size: 11px; color: var(--text-secondary); }

/* section progress dots */
.dots { display: inline-flex; gap: 4px; }
.dot { width: 9px; height: 9px; border-radius: 50%; background: rgba(255,255,255,0.14); display: inline-block; }
.dot.d-ready { background: var(--success); }
.dot.d-failed { background: var(--danger); }

/* report detail */
.rep-detail { padding: 14px 16px 20px; }
.rd-section { margin-bottom: 18px; }
.rd-title { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-secondary); margin-bottom: 8px; }
.reader-three { display: flex; flex-wrap: wrap; gap: 8px; }
.three-pill { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; padding: 4px 10px; border-radius: 12px; background: var(--field-bg); border: 1px solid var(--panel-border); }
.three-pill span { color: var(--text-secondary); font-size: 11px; }
.three-pill b { color: var(--text-primary); font-weight: 500; }

/* five places table */
.table-scroll { width: 100%; overflow-x: auto; }
.places-table { width: 100%; border-collapse: separate; border-spacing: 0; }
.places-table th, .places-table td { text-align: left; padding: 8px 10px; border-bottom: 1px solid var(--border-soft); font-size: 12.5px; vertical-align: middle; }
.places-table th { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-secondary); font-weight: 600; }
.places-table td { color: var(--text-primary); }
.places-table th.num, .places-table td.num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
.places-table tbody tr { cursor: pointer; transition: background-color 0.2s ease; }
.places-table tbody tr:hover { background: var(--hover-bg); }
.places-table tbody tr.active { background: rgba(108, 99, 255, 0.14); }
.pt-rank { font-weight: 700; color: var(--text-secondary); width: 20px; }
.pt-city { font-weight: 600; white-space: nowrap; }
.pt-country { font-size: 11px; color: var(--text-secondary); white-space: nowrap; }
.city-photo { width: 44px; height: 44px; border-radius: 8px; overflow: hidden; background: var(--panel-bg); border: 1px solid var(--panel-border); }
.city-photo img { width: 100%; height: 100%; object-fit: cover; }
.city-nophoto { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 18px; color: var(--text-secondary); }
.line-chips { display: flex; flex-wrap: wrap; gap: 4px; }
.line-chip { font-size: 11px; padding: 2px 8px; border-radius: 10px; border: 1px solid var(--panel-border); background: var(--panel-bg); color: var(--text-primary); white-space: nowrap; }
.line-chip.tone-supportive { background: rgba(74, 222, 128, 0.12); border-color: rgba(74, 222, 128, 0.3); color: var(--success); }
.line-chip.tone-mixed { background: rgba(248, 191, 113, 0.12); border-color: rgba(248, 191, 113, 0.32); color: var(--warning); }
.line-chip.tone-challenging { background: rgba(255, 107, 107, 0.12); border-color: rgba(255, 107, 107, 0.32); color: var(--danger); }

/* directions */
.dir-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 12px; }
.dir-card { border: 1px solid var(--panel-border); border-radius: var(--radius); background: var(--field-bg); padding: 12px 14px; }
.dir-head { display: flex; align-items: baseline; gap: 8px; margin-bottom: 6px; }
.dir-kind { font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--primary-color); font-weight: 700; }
.dir-city { font-weight: 600; font-size: 13.5px; color: var(--text-primary); }
.dir-title { font-weight: 600; font-size: 13px; color: var(--text-primary); margin-bottom: 4px; }
.dir-lead { margin-bottom: 6px; }

/* place prose cards */
.place-cards { display: flex; flex-direction: column; gap: 8px; }
.place-card { border: 1px solid var(--panel-border); border-radius: var(--radius); background: var(--field-bg); }
.place-card.open { border-color: rgba(108, 99, 255, 0.45); }
.pc-head { display: flex; align-items: center; gap: 8px; padding: 10px 14px; cursor: pointer; }
.pc-rank { font-weight: 700; color: var(--text-secondary); width: 14px; }
.pc-city { font-weight: 600; font-size: 13.5px; color: var(--text-primary); white-space: nowrap; }
.pc-theme { font-size: 12.5px; color: var(--text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; min-width: 0; }
.pc-state { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.4px; color: var(--text-secondary); }
.pc-state.ps-ready { color: var(--success); }
.pc-state.ps-failed { color: var(--danger); }
.pc-body { padding: 0 16px 14px 36px; }
.chart-lines { margin: 8px 0 4px; display: flex; flex-direction: column; gap: 5px; }
.cl-row { display: grid; grid-template-columns: 160px 150px 1fr; gap: 10px; font-size: 12.5px; align-items: baseline; }
.cl-name { font-weight: 600; color: var(--text-primary); }
.cl-km { font-size: 11.5px; }
.cl-detail { color: var(--text-secondary); line-height: 1.45; }
.dev-row { font-size: 12.5px; margin-bottom: 5px; line-height: 1.5; color: var(--text-primary); }

.trait-row { display: flex; flex-wrap: wrap; gap: 5px; margin: 4px 0 6px; }
.trait-chip { font-size: 11px; padding: 2px 8px; border-radius: 10px; background: var(--panel-bg); border: 1px solid var(--panel-border); color: var(--text-primary); }
.mc-mini-title { font-size: 12.5px; font-weight: 600; color: var(--text-primary); margin: 12px 0 4px; }
.mc-note { font-size: 12.5px; line-height: 1.5; color: var(--text-secondary); margin: 0; }
.mc-body { font-size: 13px; line-height: 1.55; color: var(--text-primary); margin: 0 0 6px; }
.try-list { margin: 4px 0 0; padding-left: 18px; }
.try-list li { font-size: 12.5px; line-height: 1.5; color: var(--text-primary); margin-bottom: 3px; }
.mc-pending { margin-top: 6px; font-size: 12.5px; color: var(--text-secondary); }

.parts-grid { display: flex; flex-wrap: wrap; gap: 6px; }
.part-chip { display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; padding: 3px 9px; border-radius: 12px; border: 1px solid var(--panel-border); background: var(--field-bg); color: var(--text-secondary); }
.part-chip.ps-ready { background: rgba(74, 222, 128, 0.14); color: var(--success); border-color: rgba(74, 222, 128, 0.32); }
.part-chip.ps-failed { background: rgba(255, 107, 107, 0.16); color: var(--danger); border-color: rgba(255, 107, 107, 0.38); }
.part-at { font-size: 9.5px; opacity: 0.7; }
.rd-run-meta { display: flex; gap: 16px; flex-wrap: wrap; margin-top: 10px; font-size: 12px; color: var(--text-secondary); }
.rd-run-meta code { background: var(--field-bg); padding: 1px 6px; border-radius: 4px; font-size: 11px; }

/* expanded per-slice detail (generations) */
.slice-detail { padding: 14px 16px 16px; }
.slice-table { width: 100%; border-collapse: separate; border-spacing: 0; }
.slice-table th, .slice-table td { text-align: left; padding: 7px 10px; border-bottom: 1px solid var(--border-soft); font-size: 12.5px; }
.slice-table th { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-secondary); font-weight: 600; }
.slice-table td { color: var(--text-primary); }
.slice-table tr:last-child td { border-bottom: none; }
.sl-name { font-weight: 600; }
.sl-model { color: var(--text-secondary); font-family: monospace; font-size: 11.5px; }
.slice-missing td { opacity: 0.45; }
.ok-yes { color: var(--success); font-weight: 700; }
.ok-no { color: var(--danger); font-weight: 700; }
.slice-foot { display: flex; gap: 18px; flex-wrap: wrap; margin-top: 10px; font-size: 12px; color: var(--text-secondary); }
.slice-foot code { background: var(--field-bg); padding: 1px 6px; border-radius: 4px; font-size: 11px; }
</style>
